import { APIProvider } from '@vis.gl/react-google-maps';
import React from 'react';
import { Button, Card, CloseButton, Stack } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Course } from '../../../types/course.type';
import { sessionTypes } from '../../../utils/consts/schedule/sessions';
import { CourseDateTimes, CourseClassCountAndFormat, course_type_style_lookup } from './CourseDates';
import { CourseVenueDistance } from './CourseVenueDistance';
import AddFavorite from './details/AddFavorite';

export const course_type_card_style_lookup = (key: string) => {
  switch (key) {
    case 'after_school':
      return 'pink';
    case 'camp':
      return 'orange';
    case 'event':
      return 'green';
    default:
      return 'primary';
  }
};

export interface CourseCardProps {
  course: Course;
  selected?: boolean;
  onClose?: () => void;
  onClick?: () => void;
}

export const CourseCard: React.FC<CourseCardProps> = ({ course, selected, onClose, onClick }) => {
  const navigate = useNavigate();

  let className = course_type_card_style_lookup(course.course_type);
  let fillStyle = course_type_style_lookup(course.course_type);

  const enrollment = course.enrollment?.split('/');
  const enrolled = +enrollment?.[0];
  const capacity = +enrollment?.[1];
  const seatsLeft = capacity - enrolled;
  const registerAtVenue = course.payment_type === 'onsite';
  const isSoldOut = !registerAtVenue && !isNaN(capacity) && capacity > 0 && enrolled >= capacity;
  const showSeatsLeft = !registerAtVenue && !isNaN(capacity) && capacity > 0 && !isSoldOut && seatsLeft < 5;

  return (
    <Card
      className={`course-card ${className}`}
      onClick={() => (selected ? undefined : onClick?.())}
    >
      <Card.Body>
        <Stack direction="horizontal">
          <Stack className="flex-grow-1">
            {selected && (
              <div className={`text-uppercase course-event-badge mb-3 ${className}`}>
                You Selected
              </div>
            )}
            <Card.Subtitle className={`course-card-title ${className}`}>
              {course.title}
            </Card.Subtitle>
            {selected && (
              <>
                <span className="text-muted">Theme</span>
                <Card.Subtitle>
                  <b>{course.theme.name}</b>
                </Card.Subtitle>
              </>
            )}
            <CourseDateTimes course={course} />
            <CourseClassCountAndFormat course={course} />
            <APIProvider apiKey={process.env.REACT_APP_GOOGLE_API_KEY!}>
              <CourseVenueDistance
                course={course}
                color={className}
                fillStyle={fillStyle}
              />
            </APIProvider>
          </Stack>
          {selected && (
            <Stack
              className="align-items-end"
              gap={1}
            >
              <CloseButton onClick={() => onClose?.()} />
              <AddFavorite
                course_id={course.id}
                show_label={false}
              />
            </Stack>
          )}
        </Stack>
        <Stack direction="horizontal" gap={2}>
          <div className={`text-uppercase course-event-badge ${className}`}>
            {sessionTypes[course.course_type]}
          </div>
          {isSoldOut ? (
              <span className="text-uppercase fw-bold" style={{ color: '#B81F69', fontSize: '.75rem' }}>
                Sold Out
              </span>
                      ) : showSeatsLeft && (
                          <span className="text-muted" style={{ fontSize: '.875rem' }}>
                Only {seatsLeft} {seatsLeft === 1 ? 'seat' : 'seats'} left
              </span>
          )}
          <Button
              variant={isSoldOut ? 'outline-primary' : 'primary'}
              size="sm"
              className={'text-uppercase ms-auto'}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/sessions/${course.slug ?? course.id}`);
              }}
          >
            {isSoldOut ? 'Join the Waitlist' : 'View Details'}
          </Button>
        </Stack>
      </Card.Body>
    </Card>
  );
};
